import { useTranslate } from "@refinedev/core";
import {
  Badge,
  Button as AntButton,
  Flex,
  Skeleton,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import type {
  MediaProjectResponse,
  TDetailedSection,
  DisplayMetrics,
  TScript,
  StoryboardAsset,
  SectionUpdate,
  TDetailedStoryboard,
} from "pages/media/types";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { EditOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useStoryboardWsStore } from "@/store/storyboardWsStore";
import {
  Active,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  Over,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { countScriptWords } from "pages/media/helpers/scriptAnchors";
import {
  extractHighlightIds,
  generateId,
  storyboardDetailedToStoryboard,
  storyboardToStoryboardDetailed,
} from "./editor/helpers";
import { WebSocketMessage } from "../chat/types";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import type { DisplayMode, TCallbackAssociation } from "./editor/types";
import { Section } from "./editor/Section";
import { MiniLabel } from "components/MiniLabel";
import { SPEECH_WORDS_PER_MINUTE } from "pages/media/constants";
import { Present } from "./editor/Present";
import { useVolatileBoundStore } from "store";
import { useFeature } from "hooks/useFeature";
import { SlidersIcon } from "@phosphor-icons/react";
import { SortedChaptersModal } from "./editor/SortedChaptersModal";
import { useAntTheme } from "hooks/useAntTheme";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { HighlightRankFilter } from "./editor/HighlightRankFilter";
import { FeatureFlags } from "@/constants";
import useIsAdmin from "hooks/useIsAdmin";
import { useRemoveHighlight } from "../animator/useRemoveHighlight";
import { useHighlightsStore } from "../animator/useHighlightsStore";
import { useCuepointsStore } from "../animator/useCuepointsStore";
import { useStoryboard } from "hooks/useStoryboard";
import { TTSProvider } from "@/providers/TTSProvider";
import { useAssetById } from "hooks/useAssetById/";
import { Button } from "@libertify/ui";

export const StoryboardEditor = ({
  project,
  storyboardId,
  displayMode,
  style,
}: {
  project?: MediaProjectResponse;
  storyboardId?: string;
  displayMode: DisplayMode;
  style: CSSProperties;
}) => {
  const t = useTranslate();
  const { isEnabled: isMetricsEnabled } = useFeature(
    FeatureFlags.StoryboardMetrics
  );
  const {
    setScriptsWordsCount,
    setReadingTime,
    setMetrics,
    setStoryboard,
    resetStoryboardState,
    storyboard,
    addUpdate,
  } = useVolatileBoundStore((state) => ({
    setScriptsWordsCount: state.setScriptsWordsCount,
    setReadingTime: state.setReadingTime,
    setMetrics: state.setMetrics,
    setStoryboard: state.setStoryboard,
    resetStoryboardState: state.resetStoryboardState,
    storyboard: state.storyboardState.storyboard,
    addUpdate: state.addUpdate,
  }));

  const { removeHighlight } = useRemoveHighlight(project?.id ?? "");
  const [openSortChapters, setOpenSortChapters] = useState(false);
  const [sortedStoryboard, setSortedStoryboard] =
    useState<TDetailedStoryboard>();
  const [isReady, setIsReady] = useState(false);
  const [sectionsId, setSectionsId] = useState<string[]>([]);
  const [callbacks, setCallbacks] = useState<TCallbackAssociation[]>([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = useIsAdmin();
  const { highlights, sectionHighlights } = useHighlightsStore();
  const {
    updateCount: updateCountHighlights,
    setUpdateCount: setUpdateCountHighlights,
  } = useCuepointsStore();

  const [editing, setEditing] = useState(false);
  useEffect(() => {
    const initializeStoryboard = async () => {
      setIsReady(false);
      resetStoryboardState();
      setSectionsId([]);
      setCallbacks([]);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsReady(true);
    };
    initializeStoryboard().catch((error) =>
      console.error("Error initializing storyboard", error)
    );
  }, [storyboardId, resetStoryboardState]);

  // Ref to hold the latest onMessage function to avoid stale closures
  const onMessageRef = useRef<(lastResponse: WebSocketMessage) => void>();

  //  when getting very fast updates lastResponse state seemed to lose some updates
  const onMessage = (lastResponse?: WebSocketMessage) => {
    if (!lastResponse) return;
    if (
      lastResponse.action &&
      (lastResponse.action === "CreateSection" ||
        lastResponse.action === "RegenerateSection") &&
      "section" in lastResponse &&
      lastResponse.section
    ) {
      const { section_id: id, ...newSection } = lastResponse.section;
      handleChangeSection({
        // update local after conversion
        ...newSection,
        id,
        // surface the generated script in the scripts list the UI renders
        scripts: newSection.script?.content
          ? [{ ...newSection.script, selected: true }]
          : [],
      });
    }
    if (
      lastResponse.action === "GenerateScript" &&
      "scripts" in lastResponse &&
      lastResponse.scripts &&
      "status" in lastResponse &&
      lastResponse.status === "success"
    ) {
      const call = callbacks.find(
        (item) => item.query_id === lastResponse.query_id
      );

      // Pre-select the first alternative so the chapter always has a final
      // script — the user can switch to another version if they prefer.
      const detailedScripts = lastResponse.scripts.map(
        (script: TScript, index: number) => ({
          ...script,
          selected: index === 0,
        })
      );

      call?.callback(detailedScripts);
    }
    if (
      lastResponse.action === "ImproveScript" &&
      "message" in lastResponse &&
      "script" in lastResponse &&
      lastResponse.script &&
      "status" in lastResponse &&
      lastResponse.status === "success"
    ) {
      const call = callbacks.find(
        (item) => item.query_id === lastResponse.query_id
      );
      const { sectionId, scriptIndex } = call?.callback([]) as unknown as {
        sectionId: string;
        scriptIndex: number;
      };
      setStoryboard((old) => ({
        ...old,
        title: old?.title ?? "",
        sections:
          old?.sections.map((section) => {
            if (section.id === sectionId) {
              const isSelectedScript = section.scripts?.[scriptIndex]?.selected;
              const content = lastResponse.script as string;
              return {
                ...section,
                ...(isSelectedScript
                  ? { script: { content, selected: true } }
                  : {}),
                scripts:
                  section.scripts?.map((script, index) => {
                    if (!script) return script;
                    if (index === scriptIndex)
                      return {
                        // merge values
                        ...script,
                        content,
                        // selected: false,
                        revisions: [
                          {
                            content,
                            prompt: lastResponse.message,
                          },
                          ...(script.revisions ?? [
                            {
                              content: script.content ?? "",
                              prompt: "",
                            },
                          ]),
                        ],
                      };
                    else {
                      return script;
                    }
                  }) ?? [],
              };
            } else {
              return section;
            }
          }) ?? [],
      }));
    }

    if (
      lastResponse.action === "ComputeMetrics" &&
      "metrics" in lastResponse &&
      "status" in lastResponse &&
      lastResponse.status === "success"
    ) {
      setMetrics(lastResponse.metrics as DisplayMetrics);
    }
  };

  // Use the storyboard WebSocket store
  const {
    isConnected,
    isConnecting,
    isBusy,
    requestGenerateScript,
    requestRegenerateSection,
    requestCreateSection,
    requestUpdateSection,
    requestImproveScript,
    requestComputeMetrics,
    getLastMessage,
    addMessageHandler,
  } = useStoryboardWsStore();

  const lastMessage = getLastMessage();

  const getConnectionStatus = () => {
    if (isConnected) return "Open";
    if (isConnecting) return "Connecting";
    return "Closed";
  };

  const { data: storyboardAsset } = useAssetById<StoryboardAsset>({
    assetId: storyboardId,
  });

  // Use useStoryboard hook to load storyboard data (same as VoiceoverNarrationCard)
  // This ensures both components use React Query and stay in sync
  const { data: storyboardData } = useStoryboard({
    path: storyboardAsset?.data.path,
  });

  const generateSectionScripts = (
    sectionId: string,
    callback: (scripts: TScript[]) => void
  ) => {
    if (!storyboardAsset?.data.id) return; // todo

    const queryId = requestGenerateScript(
      storyboardDetailedToStoryboard(storyboard),
      sectionId
    );

    if (!queryId) return;

    setCallbacks((old) => [...old, { query_id: queryId, callback }]);
  };

  const generateSection = (topic: string, sectionId: string) => {
    if (sectionId.startsWith("section")) {
      // local id // TODO improve logic
      requestCreateSection(topic, storyboardDetailedToStoryboard(storyboard));
    } else {
      requestRegenerateSection(
        sectionId,
        topic,
        storyboardDetailedToStoryboard(storyboard)
      );
    }
  };

  const handleImproveScript = (
    sectionId: string,
    script: TScript,
    message: string,
    callback: (scripts: TScript[]) => void
  ) => {
    const queryId = requestImproveScript(sectionId, script, message);

    if (!queryId) return;

    setCallbacks((old) => [...old, { query_id: queryId, callback }]);
  };

  const computeMetrics = () => {
    if (!isMetricsEnabled) {
      return;
    }
    requestComputeMetrics(storyboardDetailedToStoryboard(storyboard));
  };

  const extractSectionsIds = () => {
    if (!storyboard) {
      return [];
    }
    return storyboard.sections.flatMap((elt) => elt.id);
  };

  // Collect highlight ids across every alternative of a section, not just the
  // selected script. Switching between alternatives changes the selected
  // script's content, but the highlights still live in the other alternatives,
  // so they must not be treated as deleted.
  const extractSectionHighlightIds = (section: TDetailedSection): string[] => {
    const ids = (section.scripts ?? [])
      .flatMap((script) => extractHighlightIds(script?.content ?? ""))
      .concat(extractHighlightIds(section.script?.content ?? ""));
    return Array.from(new Set(ids));
  };

  const removeHighlightsFromSection = (
    oldSection: TDetailedSection,
    newSection: TDetailedSection
  ) => {
    const oldIds = extractSectionHighlightIds(oldSection);
    const newIds = extractSectionHighlightIds(newSection);

    const missingIds = oldIds.filter((id) => !newIds.includes(id));

    if (missingIds.length === 0) return;
    setUpdateCountHighlights(updateCountHighlights + 1);

    missingIds.forEach((id) => {
      const sectionHighlight = sectionHighlights.find(
        (highlight) => highlight.id === id
      );
      if (!sectionHighlight) return;
      const currentHighlight = highlights.find(
        (highlight) => highlight.id === sectionHighlight.sourceId
      );
      if (!currentHighlight) return;
      removeHighlight(currentHighlight);
    });
  };

  const handleChangeSection = (
    newSection: TDetailedSection,
    isSection = true
  ) => {
    // Both setters accept an updater that returns a non-undefined storyboard:
    // the store setter requires it, and the useState setter allows it.
    type StoryboardSetter = (
      update: (previous: TDetailedStoryboard | undefined) => TDetailedStoryboard
    ) => void;

    const storyboardOptions: Record<
      "section" | "sorted",
      [TDetailedStoryboard | undefined, StoryboardSetter]
    > = {
      section: [storyboard, setStoryboard],
      sorted: [sortedStoryboard, setSortedStoryboard],
    };

    const storyboardType = isSection ? "section" : "sorted";

    const [currentStoryboard, setCurrentStoryboard] =
      storyboardOptions[storyboardType];

    if (!currentStoryboard) {
      return;
    }
    // delete use case
    if (newSection.scripts?.length === 0 && newSection.topic === "") {
      setCurrentStoryboard((old) => {
        const base = old ?? currentStoryboard;
        return {
          ...base,
          sections: base.sections.filter((section: TDetailedSection) => {
            return section.id !== newSection.id;
          }),
        };
      });
      addUpdate();
      return;
    }

    console.debug("update section", { newSection });
    setCurrentStoryboard((value) => {
      const base = value ?? currentStoryboard;
      return {
        ...base,
        sections: base.sections.map((section: TDetailedSection) => {
          if (
            section.id === newSection.id ||
            //not a new chapter
            (section.topic !== "" && section.topic === newSection.topic)
          )
            return newSection;
          else return section;
        }),
      };
    });
    const oldSection = currentStoryboard.sections.find(
      (section) => section.id === newSection.id
    );
    const newValues: SectionUpdate = {};
    if (
      oldSection &&
      oldSection.script?.content !== newSection.script?.content
    ) {
      newValues.script = newSection.script?.content ?? "";
    }
    if (!newSection.id.startsWith("section")) {
      // sync server generated sections
      setCurrentStoryboard((old) => {
        const base = old ?? currentStoryboard;
        const sections = base.sections.map((section) => {
          if (section.id === newSection.id) {
            return {
              ...section,
              ...newSection,
            };
          }
          return section;
        });
        return {
          ...base,
          sections,
        };
      });
      requestUpdateSection(newSection.id, newValues);
    }
    if (oldSection) {
      removeHighlightsFromSection(oldSection, newSection);
    }

    addUpdate();
  };

  const handleRemoveSection = (
    oldSection: TDetailedSection,
    newSection: TDetailedSection
  ) => {
    removeHighlightsFromSection(oldSection, newSection);
    handleChangeSection(newSection);
  };

  const switchPlaces = (
    current: TDetailedSection[],
    oldIndex: number,
    newIndex: number
  ) => {
    const amputated = [
      ...current.slice(0, oldIndex),
      ...current.slice(oldIndex + 1, current.length),
    ];

    const final = [
      ...amputated.slice(0, newIndex),
      current[oldIndex],
      ...amputated.slice(newIndex, current.length),
    ];

    return final;
  };

  const moveSection = (
    active: Active,
    over: Over | null
  ): TDetailedSection[] => {
    if (!sortedStoryboard) {
      return [];
    }
    const current = sortedStoryboard.sections;
    const oldIndex = current.findIndex((elt) => elt.id === active.id);
    const newIndex = current.findIndex((elt) => elt.id === over?.id);

    const final = switchPlaces(current, oldIndex, newIndex);

    return final as TDetailedSection[];
  };

  const handleOnSortCancel = () => {
    setSortedStoryboard(storyboard);
    setSectionsId(extractSectionsIds());
    setOpenSortChapters(false);
  };

  const handleOnSortSubmit = () => {
    if (!sortedStoryboard) return;
    setStoryboard(sortedStoryboard);
    setSectionsId(sortedStoryboard.sections.flatMap((elt) => elt.id));
    setOpenSortChapters(false);
    addUpdate();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const final = moveSection(active, over);
      setSortedStoryboard((old) => {
        if (!old) return old;
        return {
          ...old,
          sections: final,
        };
      });
      setSectionsId(final.map((elt) => elt.id));
    }
  };

  // Initialize storyboard WebSocket store
  useEffect(() => {
    if (project?.id && storyboardAsset?.data.id) {
      useStoryboardWsStore.getState().init({
        organizationId: project.organization_id,
        documentIds: project.documents.map((doc) => doc.id),
        projectId: project.id,
        storyboardId: storyboardAsset.data.id,
      });
    }
    return () => {
      useStoryboardWsStore.getState().disconnect();
    };
  }, [project, storyboardAsset?.data.id]);

  // Keep onMessage ref updated to avoid stale closures
  onMessageRef.current = onMessage;

  // Override store handler with onMessage function
  useEffect(() => {
    const unsubscribe = addMessageHandler((message) => {
      onMessageRef.current?.(message as unknown as WebSocketMessage);
    });
    return unsubscribe;
  }, [addMessageHandler]);

  // Sync storyboard data from React Query to local state
  useEffect(() => {
    if (storyboardData) {
      const detailedStoryboard = storyboardToStoryboardDetailed(storyboardData);

      setStoryboard({
        ...detailedStoryboard,
        sections: detailedStoryboard.sections,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyboardData]);

  useEffect(() => {
    // add necessary ids for the ui
    setSectionsId(extractSectionsIds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyboard]);

  const sectionsWithSelectedScript = storyboard?.sections
    .filter((x) => x.scripts?.some((x) => x?.selected))
    .flatMap((x) => x.scripts?.filter((x) => x?.selected));

  const scriptsWordsCount =
    sectionsWithSelectedScript
      ?.map((x) => countScriptWords(x?.content ?? ""))
      .reduce((acc, count) => acc + count, 0) ?? 0;
  const readingTime = (60000 * scriptsWordsCount) / SPEECH_WORDS_PER_MINUTE; // seconds

  const completionPercent =
    ((sectionsWithSelectedScript?.length ?? 0) * 100) /
    (storyboard?.sections.length ?? 0);

  const finalScript = sectionsWithSelectedScript?.reduce(
    (acc, x) => acc.concat(x?.content ?? ""),
    ""
  );

  useEffect(() => {
    setScriptsWordsCount(scriptsWordsCount);
    setReadingTime(readingTime);
  }, [scriptsWordsCount, readingTime, setScriptsWordsCount, setReadingTime]);

  useEffect(() => {
    // recompute metrics when then final script changes
    if (completionPercent === 100) {
      computeMetrics();
    }
  }, [finalScript, completionPercent]);

  const { theme } = useAntTheme();

  const Status = (
    <Space size="small">
      <MiniLabel>
        {t("components.storyboard.StoryboardEditor.status")}
      </MiniLabel>
      <div style={{ position: "relative", width: 24, aspectRatio: 1 }}>
        <LoadingOutlined
          spin
          style={{
            fontSize: 24,
            opacity: isBusy || getConnectionStatus() === "Connecting" ? 1 : 0,
            position: "absolute",
            left: 0,
            top: 0,
            color: isConnected ? "green" : "red",
          }}
        />
        <Tooltip
          title={
            <>
              <div>{getConnectionStatus()}</div>
              {lastMessage?.is_final === false && (
                <div>
                  {t("components.storyboard.StoryboardEditor.loading")}
                  {lastMessage.action} {lastMessage.topic}
                </div>
              )}
            </>
          }
        >
          <Badge
            dot
            color={isConnected ? "green" : "red"}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        </Tooltip>
      </div>
    </Space>
  );

  const Controls = (
    <Flex
      align="center"
      justify="end"
      style={{
        margin: "10px 0",
      }}
    >
      {Status}
    </Flex>
  );

  if (!storyboard) {
    return (
      <>
        {Controls}
        {isBusy && <Skeleton active style={{ width: "100%" }} />}
        {isBusy && <Skeleton active style={{ width: "100%" }} />}
        {isBusy && <Skeleton active style={{ width: "100%" }} />}
        {isBusy && <Skeleton active style={{ width: "100%" }} />}
        {isBusy && <Skeleton active style={{ width: "100%" }} />}
      </>
    );
  }

  const handleAddSection = (insertAfterIndex: number) => {
    const newSecId = generateId("section");

    setStoryboard((old) => {
      const base = old ?? storyboard;
      return {
        ...base,
        sections: [
          ...base.sections.slice(0, insertAfterIndex + 1), // Keep sections before the specified index (inclusive)
          {
            topic: "",
            scripts: [],
            script: {
              content: "",
              selected: false,
            },
            id: newSecId,
          },
          ...base.sections.slice(insertAfterIndex + 1), // Keep sections after the specified index
        ],
      };
    });

    setSectionsId((old) => {
      return [
        ...old.slice(0, insertAfterIndex + 1),
        newSecId,
        ...old.slice(insertAfterIndex + 1),
      ];
    });
  };

  if (displayMode === "viewing") {
    return (
      <Flex vertical style={style}>
        {isAdmin && <HighlightRankFilter />}
        <Present storyboard={storyboard} />
      </Flex>
    );
  }
  if (!isReady) {
    return <Spin fullscreen />;
  }

  return (
    <TTSProvider>
      {Controls}
      <Flex
        vertical
        gap={10}
        style={{ marginTop: 0, ...style }}
        ref={containerRef}
      >
        {isAdmin && <HighlightRankFilter />}
        <Flex justify="space-between" align="center">
          <Typography.Title
            level={4}
            style={{ width: "90%", padding: 0, margin: 0 }}
            editable={{
              enterIcon: null,
              icon: <EditOutlined style={{ color: theme.colorPrimary }} />,
              triggerType: ["text", "icon"],
              onChange: (value) => {
                setStoryboard((old) => ({
                  ...(old ?? storyboard),
                  title: value,
                }));
                setEditing(false);
              },
              editing: editing,
              tooltip: false,
              onStart: () => setEditing(true),
            }}
          >
            {storyboard.title}
          </Typography.Title>
          <AntButton
            size="middle"
            icon={<SlidersIcon />}
            onClick={() => {
              setSortedStoryboard(storyboard); // update SB that will be sorted to current storyboard
              setOpenSortChapters(true);
            }}
          >
            {t("components.storyboard.StoryboardEditor.sortChapters")}
          </AntButton>
        </Flex>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <AddChapterButton
            handleOnClick={() => handleAddSection(-1)}
            projectId={project?.id}
            storyboardId={storyboardId}
            storyboardSectionsCount={storyboard.sections.length}
            sectionIndex={0}
          />
          {storyboard.sections.map(
            (section: TDetailedSection, index: number) => {
              return (
                <Flex vertical key={section.id} gap={20}>
                  <Section
                    generateId={generateId}
                    changeSection={handleChangeSection}
                    generateSectionScripts={generateSectionScripts}
                    improveScript={handleImproveScript}
                    generateSection={generateSection}
                    section={section}
                    editable={!isBusy && isConnected}
                    index={index}
                    isBusy={isBusy}
                    removeSection={handleRemoveSection}
                  />
                  <AddChapterButton
                    handleOnClick={() => handleAddSection(index)}
                    projectId={project?.id}
                    storyboardId={storyboardId}
                    storyboardSectionsCount={storyboard.sections.length}
                    sectionIndex={index}
                  />
                </Flex>
              );
            }
          )}
          {sortedStoryboard && (
            <SortedChaptersModal
              changeSection={(newSection) => {
                handleChangeSection(newSection, false);
              }}
              open={openSortChapters}
              sectionsId={sectionsId}
              storyboard={sortedStoryboard}
              onSubmit={handleOnSortSubmit}
              onCancel={handleOnSortCancel}
            />
          )}
        </DndContext>
      </Flex>
    </TTSProvider>
  );
};

interface AddChapterButtonProps {
  handleOnClick: () => void;
  projectId?: string;
  storyboardId?: string;
  storyboardSectionsCount?: number;
  sectionIndex: number;
}

const AddChapterButton: React.FC<AddChapterButtonProps> = ({
  handleOnClick,
  projectId,
  storyboardId,
  storyboardSectionsCount,
  sectionIndex,
}) => {
  const t = useTranslate();
  const { trackEvent } = useAnalytics();
  const { theme } = useAntTheme();
  const handleClick = () => {
    trackEvent(AnalyticsEvents.StoryboardSectionAdd, {
      project_id: projectId ?? "",
      storyboard_id: storyboardId ?? "",
      storyboard_sections_count: storyboardSectionsCount ?? 0,
      storyboard_section_index: sectionIndex,
    });

    handleOnClick();
  };

  return (
    <Button
      size="m"
      variant="outlined"
      icon={<PlusOutlined />}
      onClick={handleClick}
      style={{
        borderStyle: "dashed",
        borderRadius: theme.borderRadiusLG,
        color: theme.colorPrimary,
      }}
    >
      {t("components.storyboard.StoryboardEditor.addChapter")}
    </Button>
  );
};
